'use client'

import Button from '@dustin-web/microsite-components/src/button'
import {
  useTranslations,
  useScrollOffset,
  useScrollPosition,
  SiblingEvent,
} from '@dustin-web/microsite-components/src/hooks'
import scrollToElement from '@dustin-web/microsite-components/src/utils/scroll-to-element'
import { ListItemRenderer } from '@dustin-web/product-components/src/product-list'
import Link from 'next/link'
import { useState, useRef, useEffect } from 'react'
import {
  ProductForAccessoryListFragment,
  ProductForListFragment,
} from '@productsite-beta/src/graphql/generated/graphql-types'

type AccessoryGroupProps = {
  group: string
  accessories: ProductForAccessoryListFragment[]
  title?: string
  mainProductErpIdentifier?: string
  siblingScrollEvent?: SiblingEvent<string>
  setActiveMenuItem: (arg0: string) => void
  activeMenuItem: string | null | undefined
  isFirstGroup?: boolean
  onlyAvailableGroup?: boolean
}

export const AccessoryGroup = ({
  group,
  accessories = [],
  title,
  mainProductErpIdentifier,
  siblingScrollEvent,
  setActiveMenuItem,
  activeMenuItem,
  isFirstGroup,
  onlyAvailableGroup = false,
}: AccessoryGroupProps) => {
  const { translate } = useTranslations()
  const [isMoreOpen, setIsMoreOpen] = useState(onlyAvailableGroup)
  const showMoreToggle = accessories.length > 3
  const excludedGroups = ['TopAccessories', 'Header_UniqueOfferings']
  const { scrollOffset } = useScrollOffset()
  const ref = useRef<HTMLDivElement>(null)
  const indexRef = useRef<HTMLDivElement>(null)

  useScrollPosition(() => {
    const elScrolltop = ref.current?.getBoundingClientRect().top
    const shouldActivateInMenu =
      elScrolltop && elScrolltop < scrollOffset.top + 100 && elScrolltop > 0

    if (shouldActivateInMenu && activeMenuItem != group) {
      setActiveMenuItem(group)
    }
  }, [activeMenuItem])

  const categoryUrl = `/group/${accessories[0].category?.pathSlug ?? ''}`

  useEffect(() => {
    if (siblingScrollEvent == group && ref.current) {
      scrollToElement(ref.current, scrollOffset.top + 25)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siblingScrollEvent, ref])

  useEffect(() => {
    if (isFirstGroup && !activeMenuItem) {
      setActiveMenuItem(group)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isMoreOpen) {
      if (indexRef.current) {
        indexRef.current.tabIndex = -1

        indexRef.current.focus()
      }
    }
  }, [isMoreOpen])

  return (
    <div key={group} id={group} ref={ref} className={showMoreToggle ? 'u-mb-40' : 'u-mb-24'}>
      <>
        <div className="d-flex align-items-baseline justify-content-between">
          <h4
            id={`${group}-heading`}
            aria-label={`active category list ${group}`}
            tabIndex={0}
            className="h4 u-mb-16 u-pl-8">
            {title ?? group}
          </h4>
          {!excludedGroups.includes(group) && (
            <Link
              prefetch={false}
              className="u-fs-14 u-fw-normal d-none d-lg-block u-pr-8"
              href={categoryUrl}>
              {`${translate('ToCategory')} ${group}`}
            </Link>
          )}
        </div>
        <div className="row sm-gutters">
          {accessories.slice(0, 3).map((accessory, i) => (
            <div key={i} className="col-12">
              <ListItemRenderer
                key={accessory.id + i}
                product={accessory as ProductForListFragment}
                ticket={''}
                listType="extended"
                mainProductErpIdentifier={mainProductErpIdentifier}
                digitalDataOrigin="productPageAccessories"
                digitalIndex={i}
                isProductAccessories
                hTag="h4"
              />
            </div>
          ))}
          {isMoreOpen &&
            accessories.slice(3).map((accessory, i) => (
              <div key={i} ref={i === 0 ? indexRef : null} className="col-12">
                <ListItemRenderer
                  key={accessory.id + i}
                  product={accessory as ProductForListFragment}
                  ticket={''}
                  listType="extended"
                  mainProductErpIdentifier={mainProductErpIdentifier}
                  digitalDataOrigin="productPageAccessories"
                  isProductAccessories
                  digitalIndex={2 + i}
                  hTag="h4"
                />
              </div>
            ))}
          <div className="d-flex align-items-center u-mb-16 u-pl-16">
            {showMoreToggle && !isMoreOpen && (
              <Button
                leftIcon
                className="u-mb-0"
                onClick={() => setIsMoreOpen(true)}
                aria-controls={`accessory-group-${group}`}
                icon="plus"
                text={<span style={{ position: 'relative', top: 1 }}>{translate('ShowMore')}</span>}
              />
            )}
          </div>
          {!excludedGroups.includes(group) && (
            <Link prefetch={false} className="u-fs-14 u-fw-normal d-lg-none" href={categoryUrl}>
              {`${translate('ToCategory')} ${group}`}
            </Link>
          )}
        </div>
      </>
    </div>
  )
}
